/**
 * Words for the auto-approval decision log.
 *
 * `decision` is the POLICY's verdict and is immutable — a person acting never
 * rewrites it. `resolution` is what actually happened. They are two separate
 * facts and the screen shows them as two separate facts, because the whole
 * value of the log is in the rows where they DISAGREE: the policy held a
 * booking and somebody approved it anyway. Collapsing the pair into one
 * "status" would delete exactly the information the log exists to keep.
 *
 * Four states a reader must never confuse:
 *
 *   1. The policy held it and nobody has acted.
 *      `held` + `resolution: null` → amber, and it is work.
 *   2. The policy held it and a person approved it anyway.
 *      `held` + `resolution: 'approved'` + `overridden_by` → both verdicts on
 *      the row, and a sentence naming the disagreement in words.
 *   3. The policy is not switched on for this partner.
 *      `held` + `held_reason: 'auto_approval_disabled'` +
 *      `auto_approval_enabled: false` → "Not switched on", grey, and NOT the
 *      language of a hold: the policy did not decline to decide, it was never
 *      asked. Nothing is claimed about what it would have concluded, because
 *      the row does not say.
 *   4. No policy ran at all. `not_evaluated` → a purely human path.
 *
 * And one more, orthogonal to all four: a POST STEP failing is not the
 * decision failing. A failed partner accept means we said yes and it did not
 * land. The decision stands; the partner may not know. Both halves of that
 * get said out loud.
 *
 * No i18n in this repo — every string here is a hardcoded English literal,
 * same as the rest of the dashboard.
 */

import { prettifyToken } from './presentation';

/**
 * The policy's own verdict. Its colour is the verdict's polarity; what
 * actually HAPPENED is a separate badge, so a green "Auto-approved" never
 * stands in for "this booking was accepted".
 */
const DECISIONS = {
  auto_approved: { label: 'Auto-approved', bg: 'success' },
  held: { label: 'Held for review', bg: 'warning', text: 'dark' },
  auto_rejected: { label: 'Auto-rejected', bg: 'danger' },
  not_evaluated: { label: 'No policy ran', bg: 'light', text: 'dark' },
  // Not an offer verdict at all: the partner withdrew the booking. Named here
  // rather than left to `prettifyToken` so the badge reads as a deliberate
  // outcome and not as a token the screen did not recognise. Neutral, because
  // a cancellation is neither a win nor a fault on our side.
  partner_cancelled: { label: 'Partner cancelled', bg: 'secondary' },
};

const decisionBadge = (decision) => DECISIONS[decision] || {
  label: prettifyToken(decision, 'No verdict recorded'),
  bg: 'light',
  text: 'dark',
};

const RESOLUTIONS = {
  approved: { label: 'Approved', bg: 'success' },
  rejected: { label: 'Declined', bg: 'danger' },
};

/**
 * What became of the booking. `null` is not a gap in the record — it is the
 * answer: nobody has acted yet. Said that way round so an unresolved row
 * reads as outstanding rather than as missing data.
 */
export const resolutionBadge = (resolution) => RESOLUTIONS[resolution] || {
  label: 'Not acted on yet',
  bg: 'light',
  text: 'dark',
};

const POLICY_OFF_BADGE = { label: 'Not switched on', bg: 'secondary' };

const HELD_REASON_AUTO_APPROVAL_DISABLED = 'auto_approval_disabled';

/**
 * What the policy standing down means, WITHOUT inventing a verdict for it.
 *
 * The row arrives as `held` + `auto_approval_disabled`, which records that
 * the policy was not switched on — not what it would have concluded. Reading
 * "it would have approved this" off a trace of passing rules would be a guess
 * dressed as a fact on a screen somebody accepts bookings from, so the
 * sentence says what is known and points at the trace for the rest.
 */
const policyOffSentence = (decision) => {
  const prefix = 'Auto-approval is switched OFF for this partner, so the policy took no action.';

  if (decision === 'auto_approved' || decision === 'auto_rejected') {
    return `${prefix} The verdict beside it is what the rules came to, not something that was sent to anybody — the booking still has to be handled by hand.`;
  }

  return `${prefix} It logged what it saw and did nothing else. The booking still needs approving by hand, and the rule trace below shows what the rules made of it.`;
};

/**
 * The headline badge, plus whether this row is the policy standing down.
 *
 * `auto_approval_enabled === false` is checked strictly: a row simply missing
 * the flag is NOT claimed to be switched off, because claiming the policy did
 * nothing when it may have accepted a booking is the worse of the two
 * mistakes. `not_evaluated` is exempt — there was no policy to switch on.
 */
/**
 * Decisions the auto-approval flag has nothing to say about, so "the policy
 * stood down" is the wrong sentence for them however the flag was set.
 *
 * `not_evaluated` because no policy ran at all. `partner_cancelled` because
 * the cancellation handler ran and REFUSED — it declined to auto-cancel
 * inside the partner's notice window, which is an action, and the flag never
 * entered into it. Saying "the policy took no action… the booking still needs
 * approving by hand" about one is false twice over: something was decided, and
 * what the booking needs is cancelling. KiwiTaxi has the flag off and is 16 of
 * 109 live cancellation rows, so this is the common case, not the edge.
 */
const FLAG_IRRELEVANT_DECISIONS = ['not_evaluated', 'partner_cancelled'];

export const decisionHeadline = (row = {}) => {
  const policyStoodDown = row.auto_approval_enabled === false
    && !FLAG_IRRELEVANT_DECISIONS.includes(row.decision);

  if (policyStoodDown) {
    return {
      badge: POLICY_OFF_BADGE,
      policyStoodDown: true,
      sentence: policyOffSentence(row.decision),
      // With the policy off, `auto_approval_disabled` is the sentence above
      // said twice. Anything else is a real reason and still worth printing.
      showHeldReason: row.decision === 'held'
        && row.held_reason !== HELD_REASON_AUTO_APPROVAL_DISABLED,
    };
  }

  return {
    badge: decisionBadge(row.decision),
    policyStoodDown: false,
    sentence: null,
    showHeldReason: row.decision === 'held',
  };
};

/**
 * Did the outcome go against the policy? `not_evaluated` cannot disagree with
 * anything — there was no verdict.
 */
const isDisagreement = (row = {}) => {
  if (!row.resolution) return false;

  switch (row.decision) {
    case 'held':
      return true;
    case 'auto_approved':
      return row.resolution === 'rejected';
    case 'auto_rejected':
      return row.resolution === 'approved';
    default:
      return false;
  }
};

const VERDICT_IN_WORDS = {
  held: 'The policy held this for review',
  auto_approved: 'The policy approved this',
  auto_rejected: 'The policy rejected this',
};

const RESOLUTION_IN_WORDS = {
  approved: 'approved it anyway',
  rejected: 'declined it anyway',
};

/**
 * The disagreement, spelled out. Two badges side by side tell a reader that
 * the verdict and the outcome differ; they do not tell them WHO went against
 * the policy or that the verdict was deliberately left standing. Returns null
 * when there is nothing to reconcile, so an ordinary row stays quiet.
 */
export const outcomeSentence = (row = {}) => {
  if (row.decision === 'not_evaluated') {
    return `No policy ran on this booking — ${row.decided_by || 'a person'} handled it directly.`;
  }

  if (!isDisagreement(row)) return null;

  const verdict = VERDICT_IN_WORDS[row.decision] || 'The policy recorded a different verdict';
  const who = row.overridden_by || 'A person';
  const what = RESOLUTION_IN_WORDS[row.resolution] || `resolved it as ${prettifyToken(row.resolution).toLowerCase()}`;

  return `${verdict}. ${who} ${what}. The policy's verdict beside it is kept exactly as it was — the disagreement is the point of this log.`;
};

/**
 * Why a booking was held, in words somebody can act on.
 *
 * THE KEYS ARE A CLOSED CONTRACT, not a guess at the rule set. They are
 * `SYNC_HELD_REASONS` in the API's
 * `ph-core/src/Schemas/Interfaces/SyncDecision.interface.m.ts` — one value
 * per rule that can refuse, plus the flag — kept in that file's order so the
 * two lists can be read side by side. A key here the API cannot emit is copy
 * for a hold that never happens, which reads as a specification of the policy
 * and is not one; a reason here with no copy loses the screen's one chance to
 * say what to do. decisionPresentation.test.js holds both directions.
 *
 * Every line says what to DO about it. The two that are a SETTING rather than
 * a fault in the booking say so out loud, because "held" on a screen somebody
 * accepts transfers from otherwise reads as "something is wrong with this
 * one" — and then the wrong thing gets chased.
 */
export const HELD_REASON_COPY = {
  partner_not_enabled:
    'This partner has no reviewed policy entry, so the policy had nothing to judge the offer against. That is a gap in our own setup, not a problem with the booking — handle this one by hand, and ask for a policy entry if this partner keeps turning up here.',
  no_quote:
    'We could not work out our own price for this trip, so there is no margin to compare the offer against. Price it yourself before you accept.',
  duplicate:
    'A live booking already exists for this partner reference, so accepting this would leave us with two bookings for one trip. Find the existing one first, and accept this only if it is genuinely a different trip.',
  // THE TWO THAT CAN ONLY HAPPEN TO A PARTNER WHO PRICES US, and the copy now
  // says so. Where we published the rate sheet the offer is our own number
  // coming back, so both of these rules are measured, recorded and not
  // allowed to refuse — they are unreachable for those partners, and an
  // operator who sees one of these holds can take it as given that the price
  // in front of them is somebody else's.
  margin_below_floor:
    'The margin is under the floor for approving on our own. Look at the price before you accept. This hold can only happen for a partner who quotes US a price — where we publish the rate sheet instead, the margin is recorded but is never allowed to hold an offer.',
  demand_unknown:
    'The margin was fine, but our pricing has no date input — the policy cannot tell whether this date is a peak one, and on a peak date the answer could go the other way. Check the date against demand before you accept. Like the floor above, this can only happen for a partner who quotes us a price.',
  price_above_ceiling:
    'This offer is larger than the biggest booking the policy may accept on its own, so it was left for a person whatever the margin looked like. Check the price and the trip, then accept or decline.',
  auto_approval_disabled:
    'Auto-approval is not switched on for this partner, so the policy left the booking alone. This is a setting, not a problem with the booking.',
  // The ONLY reason that does not come from `rules_fired`. Every rule passed
  // and the policy was going to accept; the AI review read the booking's own
  // free text — the comment, the seat counts, the partner's status — which no
  // rule can see, and objected. So the copy must point at the review, not at
  // the rule trace: the generic fallback used to say "the rule trace below
  // shows which rule stopped it", which is false here and false in the one
  // case an operator most needs to understand.
  ai_concern:
    'Every rule passed and the policy was ready to accept, but the AI review found something in the booking\'s own words that the rules cannot see — a child seat asked for in a comment, say, or a flight number that contradicts the pickup time. Read the concern below and decide; the rules themselves had no objection.',
};

export const heldReasonCopy = (reason) => HELD_REASON_COPY[reason]
  || `${prettifyToken(reason, 'No reason was recorded')} — the rule trace below shows which rule stopped it.`;

/**
 * A RULE'S OUTCOME IN THREE STATES, NOT TWO — the one vocabulary for it.
 *
 * `pass` was only ever "this rule did not refuse", and for most rules that is
 * the same thing as "this rule was satisfied". For an ADVISORY rule it is
 * not: the partner's policy stood it down, so it could not have refused
 * whatever it measured. Rendered as a plain pass it reads as "the offer
 * cleared the floor" — which on a rate-sheet approval at a negative margin is
 * precisely backwards, and is the one sentence this screen must not imply.
 *
 * So there is a third state, and it is checked FIRST. A green tick is never
 * shown for a rule that could not have decided anything, even if a row broke
 * the contract by also carrying `pass: false`.
 *
 * Here rather than in either screen because both show rules: the drill-down
 * enumerates the whole trace and the log names the rule that settled a row.
 * Two renderings of one vocabulary — the argument `REVIEW_VERDICTS` below
 * makes at length, for the same reason.
 */
const RULE_OUTCOMES = {
  failed: {
    kind: 'failed', word: 'failed', icon: 'fa-xmark', announced: 'Failed',
  },
  advisory: {
    kind: 'advisory', word: 'recorded', icon: 'fa-circle-info', announced: 'Recorded, did not gate',
  },
  pass: {
    kind: 'pass', word: 'pass', icon: 'fa-check', announced: 'Passed',
  },
};

export const ruleOutcome = (rule = {}) => {
  if (rule?.advisory === true) return RULE_OUTCOMES.advisory;

  return rule?.pass === false ? RULE_OUTCOMES.failed : RULE_OUTCOMES.pass;
};

/**
 * What an advisory rule in a trace MEANS, said once above the trace rather
 * than repeated on every line of it.
 *
 * Both halves matter and they are said in this order on purpose: the rule did
 * run and its number is real (so nobody goes looking for a missing
 * measurement), and it could not have refused (so nobody reads the tick as a
 * price being approved on its merits).
 */
export const ADVISORY_RULE_COPY = 'One or more rules below were measured and recorded but were NOT allowed to refuse: we publish this partner’s prices, so the margin is a number to go and look at rather than a reason to make them wait. The measurement is real; the rule could not have held the offer.';

/**
 * Honest copy for `demand_context: null`, which is what it always is today:
 * our pricing has no date input at all, so "no demand data" is a permanent
 * fact about the pricing and not a gap in this booking's record.
 */
export const DEMAND_UNKNOWN_COPY = 'Not known. Our pricing has no date input, so the policy cannot see whether this date is a peak one. On a thin margin a peak date could flip the answer, so check the date yourself.';

/**
 * THE FOUR IDS THE API ACTUALLY EMITS, verbatim from `runStep` in
 * `SyncDecisionService.ts` and from `SyncPostStep`'s own comment. Checked
 * against the emitting code rather than guessed: this map had
 * `processed_at` for a step the service only ever writes as
 * `processed_at_stamp`, so the name was dead and the real step fell through
 * to the generic prettifier. `postStepName.test` pins all four.
 */
const POST_STEP_NAMES = {
  flow_stamp: 'Flow stamp',
  partner_accept: 'Partner accept call',
  booking_write: 'Booking write',
  processed_at_stamp: 'ProcessedAt stamp',
};

export const postStepName = (id) => POST_STEP_NAMES[id] || prettifyToken(id, 'Unnamed step');

/**
 * Matched on the id containing "accept" rather than on an exact value: the
 * step that tells a partner we accepted is the one whose failure is a promise
 * we did not keep, and it must not quietly lose its special copy because the
 * backend renamed it.
 */
const isPartnerAcceptStep = (id) => /accept/i.test(String(id || ''));

export const failedPostSteps = (row = {}) => (Array.isArray(row.post_steps) ? row.post_steps : [])
  .filter((step) => step && step.ok === false);

export const FOLLOW_UP_FAILED_BADGE = { label: 'Follow-up failed', bg: 'danger' };

/**
 * Said in two halves on purpose: what did not land, and then that the
 * decision still stands. A reader who takes in only the first half still goes
 * and fixes the right thing; a reader who takes in only the second does not
 * conclude the booking fell through.
 */
export const postStepFailureCopy = (step = {}, partnerName = 'the partner') => {
  if (isPartnerAcceptStep(step.id)) {
    return `We accepted this booking, but the accept call to ${partnerName} did not land — ${partnerName} may not know we have taken it. The decision itself stands; accept it by hand on the partner's portal.`;
  }

  // The booking write runs BEFORE the accept call, so its failure is the one
  // reassuring failure in this list: nothing was promised to anybody. Said
  // explicitly, because the generic sentence below reads as "a promise is
  // half-made" and sent a reader looking for a partner to phone. The write
  // used to run after the accept, and then this really was a divergence.
  if (step.id === 'booking_write') {
    return `The booking could not be created, so ${partnerName} was never told we accepted — neither side has a booking and the offer is still open. ${step.detail ? `${step.detail}. ` : ''}Create it by hand from the offer, which is still in the queue.`;
  }

  return `${postStepName(step.id)} failed after the decision was made. The decision itself stands; this step has to be done by hand.`;
};

/**
 * Whether the partner's own terms were available to the policy. KiwiTaxi has
 * no help centre loaded and is the partner that most needs a rule, so "we did
 * not read their terms" has to be visible rather than implied by silence.
 */
export const partnerDocsCopy = (partnerDocs) => {
  if (!partnerDocs || typeof partnerDocs !== 'object') {
    return { consulted: null, text: 'No record of whether this partner\'s own terms were checked.' };
  }

  const reason = partnerDocs.reason ? ` (${partnerDocs.reason})` : '';

  if (partnerDocs.consulted === true) {
    return { consulted: true, text: `Checked against this partner's own terms${reason}.` };
  }

  return {
    consulted: false,
    text: `Not available — the policy could not check what we would be agreeing to${reason}.`,
  };
};

/**
 * Who to credit. "agent" is the policy itself; anything else is an email.
 */
export const decidedByLabel = (decidedBy) => (decidedBy === 'agent' ? 'The policy' : (decidedBy || 'Not recorded'));

/**
 * Rows that are somebody's job right now.
 *
 * Keyed on `resolution`, not on the verdict: a booking nobody has acted on is
 * outstanding whatever the policy thought, and a booking somebody has acted
 * on is settled even where the policy held it. That is what makes switching
 * auto-approval on visibly take work away — an approved row arrives already
 * resolved and never joins this group — and what stops a held-then-approved
 * row sitting in it forever.
 *
 * A failed follow-up is in here too: the decision is resolved but a promise
 * to a partner is half-made.
 */
export const needsAPerson = (row = {}) => row.resolution == null
  || failedPostSteps(row).length > 0;

/**
 * WHAT THE AI REVIEWER CONCLUDED — the three verdicts, in one vocabulary.
 *
 * Here rather than in the drill-down's `AgentReview` panel because the review
 * is now on the awaiting-review queue as well: the queue shows the badge and
 * the concern kinds, the drill-down shows the badge, the blurb and the
 * evidence. Two renderings of ONE vocabulary. A short label invented for the
 * queue row would be a second spelling of a closed API enum
 * (`AGENT_REVIEW_VERDICTS` in the API's `Constants/AgentReview.ts`), which is
 * the mistake `HELD_REASON_COPY`'s header already argues against at length.
 *
 * `no_concern` IS NOT AN APPROVAL and the copy never implies it is: the API
 * contract says in as many words that it means the reviewer worked and found
 * nothing in the text. `unavailable` is the third state — the reviewer did not
 * work at all — and is said as that rather than folded into "nothing found",
 * because collapsing the two would turn every model outage into a clean bill
 * of health.
 */
export const REVIEW_VERDICTS = {
  concern: {
    badge: { label: 'Raised a concern', bg: 'warning', text: 'dark' },
    blurb: 'The reviewer read the free text and found something the rules do not look at.',
  },
  no_concern: {
    badge: { label: 'Nothing found', bg: 'light', text: 'dark' },
    blurb: 'The reviewer worked and found nothing in the text. That is not an approval — what happened to the offer is the verdict above.',
  },
  unavailable: {
    badge: { label: 'Did not run', bg: 'secondary' },
    blurb: 'The reviewer was unreachable for this decision. Recorded as a fact rather than left out, so "we did not review this" is countable.',
  },
};

/**
 * The verdict's badge and blurb, or `null` when NO REVIEW IS RECORDED AT ALL.
 *
 * Null is its own answer and the callers render it as one: `review` is never
 * absent on a decision taken since the reviewer existed (the interface says
 * so, and even an unreachable model writes `verdict: 'unavailable'`), so a
 * missing block means a decision from before it — not a reviewer that found
 * nothing.
 */
export const reviewPresentation = (review) => {
  if (!review || typeof review !== 'object') return null;

  return REVIEW_VERDICTS[review.verdict] || {
    badge: { label: prettifyToken(review.verdict, 'No verdict recorded'), bg: 'light', text: 'dark' },
    blurb: 'A verdict this screen has not been taught. The concerns below, if any, are the record.',
  };
};

/**
 * The concerns, with their kinds in words. THE ONE PLACE A CONCERN KIND IS
 * PRETTIFIED — `SYNC_CONCERN_KINDS` is a closed set of five and both the queue
 * row and the drill-down name them, so they name them identically.
 *
 * `evidence` is passed through untouched: it is the reviewer QUOTING the
 * booking, and a quote is checkable in two seconds where a paraphrase is not.
 */
export const concernList = (review) => (Array.isArray(review?.concerns) ? review.concerns : [])
  .map((concern) => ({
    kind: prettifyToken(concern?.kind, 'Unnamed concern'),
    evidence: concern?.evidence || null,
  }));
